/**
 * 文档控制钩子
 * 统一的文档 **身份** (DocumentIdentity) 控制入口点，整合上下文和属性两种方式
 *
 * 重要: 在使用此钩子前，请先阅读文档 **身份** 生命周期指南:
 * @see ../DOCUMENT_ID_LIFECYCLE.md (虽然文件名仍为DOCUMENT_ID_LIFECYCLE.md, 内容已更新为DocumentIdentity)
 *
 * 此钩子实现"上下文优先，props降级"的混合模式:
 * - 当上下文中有有效的文档 **身份** 时，优先使用上下文 **身份**
 * - 当上下文中无有效 **身份** 但提供了有效的 `propIdentity` (属性传入的文档身份) 时，使用属性 **身份**
 * - 支持通过选项配置优先级和自动激活行为
 *
 */
import { useEffect, useMemo, useCallback } from 'react';
import { DocumentIdentitySource, } from '@readsavor/types';
import { useDocumentIdentity } from '../contexts/DocumentIdentityContext.js';
/**
 * 默认选项
 */
const DEFAULT_OPTIONS = {
    autoActivatePropId: true,
    autoDeactivateOnUnmount: false,
    conflictStrategy: 'context-first',
};
/**
 * 文档控制钩子
 * 统一的文档ID获取和控制入口点
 */
export function useDocumentControl(options = {}) {
    const { propIdentity, source = DocumentIdentitySource.PROPS, metadata, autoActivatePropId: autoActivatePropIdentity = DEFAULT_OPTIONS.autoActivatePropId, autoDeactivateOnUnmount = DEFAULT_OPTIONS.autoDeactivateOnUnmount, conflictStrategy = DEFAULT_OPTIONS.conflictStrategy, } = options;
    const documentIdentity = useDocumentIdentity();
    const { currentIdentity: contextIdentityFromCtx, isValidIdentity: isContextIdentityActuallyValid, documentSource: contextSourceFromCtx, activateDocument: contextActivateDocument, deactivateDocument: contextDeactivateDocument, validateIdentity: validateIdentityFromCtx, } = documentIdentity;
    const isPropIdentityValid = useMemo(() => (propIdentity ? validateIdentityFromCtx(propIdentity) : false), [propIdentity, validateIdentityFromCtx]);
    const isContextIdentityValid = isContextIdentityActuallyValid;
    const hasConflict = useMemo(() => {
        const hasBothIdentities = isPropIdentityValid && isContextIdentityValid;
        const differentIds = propIdentity?.id !== contextIdentityFromCtx?.id;
        const differentTypes = propIdentity?.type !== contextIdentityFromCtx?.type;
        const conflict = hasBothIdentities && (differentIds || differentTypes);
        return conflict;
    }, [isPropIdentityValid, isContextIdentityValid, propIdentity, contextIdentityFromCtx]);
    const actualSource = useMemo(() => {
        if (!isContextIdentityValid && !isPropIdentityValid) {
            return 'none';
        }
        if (hasConflict) {
            return conflictStrategy === 'context-first' ? 'context' : 'prop';
        }
        return isContextIdentityValid ? 'context' : isPropIdentityValid ? 'prop' : 'none';
    }, [isContextIdentityValid, isPropIdentityValid, hasConflict, conflictStrategy]);
    const currentControlledIdentity = useMemo(() => {
        if (actualSource === 'context') {
            return contextIdentityFromCtx;
        }
        if (actualSource === 'prop') {
            return propIdentity || null;
        }
        return null;
    }, [actualSource, contextIdentityFromCtx, propIdentity]);
    useEffect(() => { }, [currentControlledIdentity, actualSource]);
    useEffect(() => {
        if (autoActivatePropIdentity &&
            isPropIdentityValid &&
            actualSource === 'prop' &&
            propIdentity) {
            contextActivateDocument(propIdentity, source, metadata);
        }
    }, [
        autoActivatePropIdentity,
        isPropIdentityValid,
        propIdentity,
        actualSource,
        contextActivateDocument,
        source,
        metadata,
    ]);
    const activateDocument = useCallback((identity, docSource = source, docMetadata) => {
        if (!validateIdentityFromCtx(identity)) {
            return false;
        }
        return contextActivateDocument(identity, docSource, docMetadata || metadata);
    }, [contextActivateDocument, validateIdentityFromCtx, source, metadata]);
    const deactivateDocument = useCallback((identity) => {
        if (identity === undefined) {
            return contextDeactivateDocument();
        }
        else {
            return contextDeactivateDocument(identity);
        }
    }, [contextDeactivateDocument]);
    const validateIdentity = useCallback((identity) => validateIdentityFromCtx(identity), [validateIdentityFromCtx]);
    useEffect(() => {
        return () => {
            if (autoDeactivateOnUnmount && currentControlledIdentity) {
                if (validateIdentityFromCtx(currentControlledIdentity)) {
                    contextDeactivateDocument(currentControlledIdentity);
                }
            }
        };
    }, [
        autoDeactivateOnUnmount,
        currentControlledIdentity,
        contextDeactivateDocument,
        validateIdentityFromCtx,
    ]);
    return {
        currentIdentity: currentControlledIdentity,
        isValidIdentity: !!currentControlledIdentity && validateIdentityFromCtx(currentControlledIdentity),
        documentSource: actualSource !== 'none' ? (actualSource === 'context' ? contextSourceFromCtx : source) : null,
        actualSource,
        hasConflict,
        propIdentity: propIdentity || null,
        contextIdentity: contextIdentityFromCtx || null,
        activateDocument,
        deactivateDocument,
        validateIdentity,
    };
}
//# sourceMappingURL=useDocumentControl.js.map